import privacyHtml from '../../../../marketing/privacy/index.html?raw';
import termsHtml from '../../../../marketing/terms/index.html?raw';
import { MARKETING_SITE_URL } from '../marketing/MarketingChrome';
import { PRIVACY_PATH, TERMS_PATH } from './legalRoutes';

/*
  One source of truth for the legal text: the marketing site's own pages.

  `marketing/terms/index.html` and `marketing/privacy/index.html` are what rosterpilot.com serves.
  Vite's `?raw` import bundles each file as a string at build time, and `extractLegalDocument` pulls
  out the parts a reader needs: the h1, the "Last updated" date, and the body inside `main#main`. So
  an edit to the legal wording is made once, in marketing/, and the next web build carries it. Never
  copy the text into JSX.

  Why rendering this HTML is safe: it is first-party, static content committed to this repo and
  reviewed like code. It is fixed at build time, so nothing a user types, and nothing the API
  returns, can reach it. It is still parsed with DOMParser (which runs no scripts and loads
  nothing) and passed through an allowlist (`stripActiveContent`: text tags, plain links, no inline
  handlers or styles) before it is rendered, so a script, frame or handler pasted into the
  marketing page by mistake would not run here either. Do not route any other content through this
  module.
*/

export type LegalDocumentKind = 'terms' | 'privacy';

export interface LegalDocument {
  /** The page heading, as the marketing page's h1 has it ("Terms of Service"). */
  title: string;
  /** ISO date from the marketing page's `<time datetime>`, e.g. "2026-08-17". */
  updatedIso: string;
  /** The date as printed, e.g. "August 17, 2026". */
  updatedLabel: string;
  /** The legal text: the inner HTML of the marketing page's main content, cleaned and re-linked. */
  bodyHtml: string;
}

const SOURCES: Record<LegalDocumentKind, string> = {
  terms: termsHtml,
  privacy: privacyHtml,
};

/** Text elements the legal pages use. Anything else is unwrapped: its text stays, the tag goes. */
const ALLOWED_ELEMENTS = new Set([
  'p', 'ul', 'ol', 'li', 'a', 'strong', 'em', 'b', 'i', 'h2', 'h3', 'h4', 'time', 'div', 'span', 'br', 'address',
]);
/** Elements dropped with their content: they could run, restyle or embed something. */
const DROPPED_ELEMENTS = 'script, style, link, meta, iframe, frame, object, embed, form, base, noscript, template, svg, math, img';
const ALLOWED_ATTRIBUTES = new Set(['href', 'rel', 'class', 'datetime']);
/** Absolute http(s), mailto, tel, a fragment, or a site-relative path. Not `//host` or `/\host`,
 *  which browsers read as another site. */
const SAFE_HREF = /^(https?:|mailto:|tel:|#|\/(?![/\\]))/i;

/** An allowlist pass: only plain text markup and plain links survive. */
const stripActiveContent = (root: Element) => {
  root.querySelectorAll(DROPPED_ELEMENTS).forEach((el) => el.remove());
  // Deepest first, so unwrapping a parent never skips a child that still needs checking.
  Array.from(root.querySelectorAll('*'))
    .reverse()
    .forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (!ALLOWED_ATTRIBUTES.has(name) || (name === 'href' && !SAFE_HREF.test(attr.value.trim()))) {
          el.removeAttribute(attr.name);
        }
      }
      if (!ALLOWED_ELEMENTS.has(el.tagName.toLowerCase())) {
        el.replaceWith(...Array.from(el.childNodes));
      }
    });
};

/** The marketing site's own paths for the legal pages, and where the web app serves them. */
const LEGAL_PATHS: Partial<Record<string, string>> = {
  '/privacy': PRIVACY_PATH,
  '/privacy/': PRIVACY_PATH,
  '/terms': TERMS_PATH,
  '/terms/': TERMS_PATH,
};

/**
 * The marketing pages link with site-relative paths. Served from the web app those would land on
 * the app, so the legal pages map to the app's own and anything else goes to the marketing site.
 * Runs after `stripActiveContent`, which has already removed `//host` and `/\host` hrefs.
 */
const relink = (root: Element) => {
  root.querySelectorAll('a[href^="/"]').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    a.setAttribute('href', LEGAL_PATHS[href] ?? new URL(href, MARKETING_SITE_URL).toString());
  });
};

/**
 * Pull the legal content out of one of the marketing site's legal pages. Throws when the page no
 * longer has the shape this expects rather than rendering an empty legal page; the build guard in
 * vite.config.ts stops such a page from deploying in the first place.
 */
export const extractLegalDocument = (html: string): LegalDocument => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const title = doc.querySelector('h1')?.textContent?.trim();
  const updated = doc.querySelector('.updated time');
  const updatedIso = updated?.getAttribute('datetime')?.trim();
  const updatedLabel = updated?.textContent?.trim();
  const body = doc.querySelector('main#main .wrap');

  if (!title || !updatedIso || !updatedLabel || !body || !body.textContent?.trim()) {
    // vite.config.ts's legalPagesGuard checks the same shape at build time, so this should only
    // ever fire in tests.
    throw new Error(
      'Legal page markup changed: expected an h1, a ".updated time" and a "main#main .wrap" in the marketing page.'
    );
  }

  stripActiveContent(body);
  relink(body);
  return { title, updatedIso, updatedLabel, bodyHtml: body.innerHTML.trim() };
};

const cache = new Map<LegalDocumentKind, LegalDocument>();

/** The parsed legal page. Parsed once per kind on first use, not at import. */
export const getLegalDocument = (kind: LegalDocumentKind): LegalDocument => {
  let parsed = cache.get(kind);
  if (!parsed) {
    parsed = extractLegalDocument(SOURCES[kind]);
    cache.set(kind, parsed);
  }
  return parsed;
};
